// ClientCardModal — la tarjeta QR de UN cliente, vista desde el negocio (23-oct-2026).
// Para dársela al cliente que ya existe (los 2,000 de siempre): mandársela por WhatsApp, copiar el
// enlace o imprimirla. El código se crea la primera vez (POST /loyalty/contacts/:id/card).
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import { X, Copy, Printer, Loader2, CreditCard, MessageCircle, Check } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../context/AuthContext';
import { cardScanApi } from '../../../api/cardScanApi';

interface Props {
  client: { id: string | number; name: string | null; phone?: string };
  onClose: () => void;
}

const QR_ID = 'qr-tarjeta-cliente';

function imprimir(negocio: string, nombre: string, code: string, url: string) {
  const svg = document.getElementById(QR_ID)?.outerHTML || '';
  const w = window.open('', '_blank', 'width=700,height=900');
  if (!w) return;
  const esc = (t: string) => t.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
  w.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Tarjeta · ${esc(nombre)}</title>
<style>
  @page { size: letter; margin: .5in }
  body { margin: 0; font-family: 'Plus Jakarta Sans', system-ui, sans-serif; color: #0E1E3A; display: flex; justify-content: center; }
  .tarjeta { width: 3.4in; border: 2px solid #0E1E3A; border-radius: 18px; padding: .3in; text-align: center; margin-top: .5in; }
  .neg { font-size: 13pt; font-weight: 800; margin: 0; }
  .nom { font-size: 11pt; color: #475569; margin: 4px 0 12px; }
  .qr svg { width: 2.2in; height: 2.2in; }
  .cod { font-family: ui-monospace, monospace; letter-spacing: .18em; font-size: 11pt; margin-top: 10px; }
  .pie { font-size: 8pt; color: #64748b; margin-top: 10px; }
</style></head><body><div class="tarjeta">
  <p class="neg">${esc(negocio)}</p>
  <p class="nom">Tarjeta de ${esc(nombre)}</p>
  <div class="qr">${svg}</div>
  <p class="cod">${esc(code)}</p>
  <p class="pie">Enséñala al pagar y suma tus visitas.<br>${esc(url)}</p>
</div><script>window.onload = () => setTimeout(() => window.print(), 300);<\/script></body></html>`);
  w.document.close();
}

export default function ClientCardModal({ client, onClose }: Props) {
  const { currentUser } = useAuth();
  const negocio = currentUser?.businessName || 'tu negocio';
  const nombre = client.name || 'Cliente';
  const [card, setCard] = useState<{ code: string; url: string } | null>(null);
  const [error, setError] = useState('');
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    let alive = true;
    cardScanApi.card(client.id)
      .then((r) => { if (alive) setCard(r); })
      .catch((e: Error) => { if (alive) setError(e.message || 'No se pudo generar la tarjeta.'); });
    return () => { alive = false; };
  }, [client.id]);

  const texto = card
    ? `Hola ${nombre.split(' ')[0]} 👋 Esta es tu tarjeta de ${negocio}: ${card.url}\nGuárdala en tu celular y enséñala al pagar para sumar tus visitas 🎁`
    : '';
  const tel = String(client.phone || '').replace(/\D+/g, '');
  const waUrl = tel ? `https://wa.me/${tel.length <= 10 ? '52' + tel : tel}?text=${encodeURIComponent(texto)}` : '';

  const copiar = async () => {
    if (!card) return;
    try { await navigator.clipboard.writeText(card.url); setCopiado(true); window.setTimeout(() => setCopiado(false), 2000); }
    catch { toast.error('No se pudo copiar. Selecciona el enlace y cópialo.'); }
  };

  return createPortal(
    <div className="modal-overlay fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-sm rounded-t-2xl sm:rounded-2xl bg-surface shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h3 className="text-base font-bold text-content flex items-center gap-2"><CreditCard className="h-5 w-5 text-accent" /> Tarjeta de {nombre}</h3>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-content-3 hover:bg-surface-2"><X size={18} /></button>
        </div>

        <div className="px-5 pb-5">
          {error ? (
            <p className="text-sm text-red-600 dark:text-red-400 py-6 text-center">{error}</p>
          ) : !card ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-content-3" /></div>
          ) : (
            <>
              {/* La tarjeta tal como la ve el cliente, en los colores del negocio */}
              <div className="rounded-2xl p-4 text-white shadow-lg"
                style={{ background: `linear-gradient(150deg, ${currentUser?.businessColors?.primary || '#0057B8'}, ${currentUser?.businessColors?.secondary || '#1e3a8a'})` }}>
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/70">Mi tarjeta</p>
                <p className="text-sm font-semibold truncate">{negocio}</p>
                <div className="mt-3 mx-auto w-fit rounded-xl bg-white p-2">
                  <QRCodeSVG id={QR_ID} value={card.url} size={168} bgColor="#ffffff" fgColor="#0E1E3A" level="M" includeMargin={false} />
                </div>
                <p className="mt-2 text-center font-mono text-xs tracking-[0.2em] text-white/80">{card.code}</p>
              </div>

              <p className="mt-3 text-xs text-content-3 text-center">
                Mándasela y que la guarde en su celular. Al pagar, te la enseña y tú la escaneas.
              </p>

              <div className="mt-3 grid grid-cols-1 gap-2">
                {waUrl && (
                  <a href={waUrl} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-2.5 text-sm font-bold text-white hover:brightness-95">
                    <MessageCircle className="h-4 w-4" /> Enviar por WhatsApp
                  </a>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={copiar}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-line px-3 py-2.5 text-sm font-medium text-content-2 hover:bg-surface-2">
                    {copiado ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />} {copiado ? 'Copiado' : 'Copiar enlace'}
                  </button>
                  <button type="button" onClick={() => imprimir(negocio, nombre, card.code, card.url)}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-line px-3 py-2.5 text-sm font-medium text-content-2 hover:bg-surface-2">
                    <Printer className="h-4 w-4" /> Imprimir
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
